"use client"

import { ReactNode } from "react"
import { CareersHeader } from "@/components/max/CareersHeader"
import { CareersFooter } from "@/components/max/CareersFooter"
import { CAREERS_MAX_W, CAREERS_INSET } from "@/lib/career/layout"

// Shared chrome for the PUBLIC careers intelligence pages (hub, indexes, role/employer/location
// pages, reports, methodology). Server pages render their content inside this so branding, the
// theme scope (.theme-max .smx), the honest "aggregate market intelligence, not personalized"
// footer disclosure, and the paid CTA are identical everywhere. Client only for the header's
// session-aware CTA + theme toggle; the page content it wraps is server-rendered.
export function PublicIntelShell({ children }: { children: ReactNode }) {
  return (
    <div className="theme-max smx" style={{ minHeight: "100vh", background: "var(--canvas)", color: "var(--text-primary)", display: "flex", flexDirection: "column" }}>
      <CareersHeader />
      <main style={{ width: "100%", maxWidth: CAREERS_MAX_W, margin: "0 auto", padding: `clamp(20px,3vw,40px) ${CAREERS_INSET} 40px`, flex: 1 }}>
        {children}
      </main>
      <CareersFooter />
    </div>
  )
}

// ── Small presentational primitives shared by the public pages (server-safe once rendered by a
//    client boundary). Token-driven so they theme with the .smx scope. ──────────────────────────

// A horizontal distribution bar row (role families, tracks, locations). Deterministic, no chart lib.
export function BarRow({ label, n, max, href }: { label: string; n: number; max: number; href?: string }) {
  const w = max > 0 ? Math.max(2, Math.round((n / max) * 100)) : 0
  const body = (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(120px, 190px) 1fr auto", gap: 12, alignItems: "center", padding: "5px 0" }}>
      <span style={{ fontSize: 13, color: href ? "var(--accent-text)" : "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
      <span style={{ height: 8, borderRadius: 999, background: "var(--surface-sunken, rgba(148,163,184,0.15))", overflow: "hidden" }}>
        <span style={{ display: "block", height: "100%", width: `${w}%`, background: "rgba(241,48,9,0.55)", borderRadius: 999 }} />
      </span>
      <span style={{ fontSize: 12.5, color: "var(--text-muted)", fontVariantNumeric: "tabular-nums", minWidth: 44, textAlign: "right" }}>{n.toLocaleString()}</span>
    </div>
  )
  return href ? <a href={href} style={{ textDecoration: "none", display: "block" }}>{body}</a> : body
}

// A caveated secondary stat: "N of M postings that state it (P%)". Used for sparse fields that
// must never headline. Renders nothing when the base is insufficient.
export function StatedLine({ label, n, denom }: { label: string; n: number; denom: number }) {
  if (denom < 30) return null
  const pct = denom > 0 ? Math.round((n / denom) * 100) : 0
  return (
    <div style={{ fontSize: 12.5, color: "var(--text-muted)", padding: "3px 0" }}>
      {label}: <strong style={{ color: "var(--text-primary)" }}>{pct}%</strong>{" "}
      <span style={{ opacity: 0.75 }}>({n.toLocaleString()} of {denom.toLocaleString()} that state it)</span>
    </div>
  )
}
